// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { ListItem, ListItemProps, UnorderedList } from "@insite/mobius/Lists";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Pagination from "@insite/mobius/Pagination";
import Select from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@werner/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import loadEvents from "@werner/App/Handlers/TrainingCalender/LoadEvents";
import setEventFilters from "@werner/App/Handlers/TrainingCalender/SetEventFilter";
import { TrainingFacetsModel, TrainingFacetValue } from "@werner/App/Services/EventService";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import TextField, { TextFieldProps } from "@werner/MobiusOverrides/TextField/TextField";
import Search from "@werner/XDL/Icons/Search";
import { color, ellipsis, getSystemColor, xScreen } from "@werner/XDL/Utilities";
import debounce from "lodash/debounce";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        events: state.trainingCalender.events,
        facets: state.trainingCalender.facets,
        pagination: state.trainingCalender.pagination,
        isLoading: state.trainingCalender.isLoading,
        selectedFilters: state.trainingCalender.selectedFilters,
        xGoogleAnalytics: (state as unknown as xGoogleAnalyticsState).xGoogleAnalytics,
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    loadEvents,
    setEventFilters,
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface TrainingCalednerListStyles {
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper: InjectableCss;
    topSection?: InjectableCss;
    searchInput?: TextFieldProps;
    outerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    eventCard?: InjectableCss;
    eventInfo?: InjectableCss;
    eventActions?: InjectableCss;
    eventTitle?: InjectableCss;
    eventTopic?: TypographyProps;
    eventTags?: TypographyProps;
    shortDescription?: TypographyProps;
    eventPrice?: TypographyProps;
    facets?: InjectableCss;
    pdf?: TypographyProps;
    pdfLink?: LinkPresentationProps;
    pagination: InjectableCss;
    courseDetailsButton?: ButtonPresentationProps;
    accordion?: AccordionPresentationProps;
    mobileFilterWrapper?: InjectableCss;
    filteredSelection?: InjectableCss;
    filteredSelectionItem?: ListItemProps;
    closeFilterIcon: IconPresentationProps;
    upcomingDateSection?: InjectableCss;
    upcomingDate?: TypographyProps;
    upcomingDateLabel?: TypographyProps;
}

export const trainingCalenderListStyles: TrainingCalednerListStyles = {
    spinnerWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            ${xScreen("lte", "xs")} {
                padding: 0;
            }
        `,
    },
    topSection: {
        css: css`
            margin-bottom: 32px;
            background-color: ${color("common.ui2")};
            padding: 30px 32px 32px;
        `,
    },
    searchInput: {
        css: css``,
        cssOverrides: {
            formField: css`
                width: 100%;
                margin-bottom: 5px;
            `,
        },
        sizeVariant: "default",
        iconProps: {
            src: Search,
            color: "primary",
        },
    },
    outerContainer: {
        offsetCss: css`
            flex-direction: column;
            padding: 10px;
            ${xScreen("lte", "xs")} {
                padding: 16px;
            }
        `,
    },
    gridItem: {
        width: [12],
        css: css`
            border-top: 1px solid ${color("common.ui1")};
            padding: 32px 0;
            display: block;
            width: 100%;
            height: 100%;
        `,
    },
    eventCard: {
        css: css`
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
            }
        `,
    },
    eventTitle: {
        css: css`
            line-height: 22px;
            margin: 0 0 7px;
            color: ${color("primary")};
            font-weight: 400;
            font-size: 22px;
            &:hover {
                color: ${color("common.ui3")};
            }
        `,
    },
    eventTopic: {
        weight: 500,
    },
    eventTags: {
        size: 16,
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            line-height: 16px;
            margin-bottom: 8px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                align-items: flex-start;
                gap: 2px;
                margin-bottom: 10px;
            }
        `,
    },
    pdf: {
        css: css`
            color: ${color("common.ui3")};
            &:hover {
                color: ${color("primary.main")};
            }
        `,
    },
    pdfLink: {
        color: "common.ui3",
        hoverStyle: {
            color: getSystemColor("primary.main"),
        },
        icon: {
            iconProps: {
                src: ChevronRight,
                color: "common.ui3",
                css: css`
                    margin-left: 0;
                `,
            },
            position: "right",
        },
        typographyProps: {
            size: 16,
            weight: 400,
        },
    },
    eventInfo: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    upcomingDateSection: {
        css: css`
            display: flex;
            align-items: baseline;
            gap: 10px;
            line-height: 16px;
            margin-bottom: 8px;
        `,
    },
    upcomingDateLabel: {
        css: css`
            flex: 0 0 auto;
        `,
    },
    shortDescription: {
        size: 15,
        css: css`
            color: ${color("text.main")};
            max-width: 700px;
            margin-bottom: 10px;
            ${ellipsis(2)}
        `,
    },
    eventPrice: {
        size: 16,
        weight: 500,
        css: css`
            color: ${color("text.main")};
            line-height: 22px;
            ${xScreen("lte", "xs")} {
                margin-bottom: 10px;
            }
        `,
    },
    eventActions: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            ${xScreen("lte", "xs")} {
                align-items: flex-start;
                gap: 2px;
                margin-bottom: 10px;
                width: 100%;
            }
        `,
    },
    facets: {
        css: css`
            text-align: right;
            line-height: 17px;
            font-size: 15px;
            margin-bottom: 16px;
            ${xScreen("lte", "xs")} {
                text-align: left;
            }
        `,
    },
    pagination: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            margin: 0 auto;
        `,
    },
    courseDetailsButton: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    accordion: {
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
                margin-bottom: 20px;
                width: 100%;
            }
        `,
    },
    mobileFilterWrapper: {
        css: css`
            margin-bottom: 20px;
            size: 16px;
        `,
    },
    filteredSelection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            list-style-type: none;
            margin: 0 0 15px 0;
            padding: 0;
        `,
    },
    filteredSelectionItem: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            background-color: ${color("common.ui3")};
            color: ${color("common.white")};
            font-weight: 500;
            border-radius: 15px;
            padding: 0 10px 0 5px;
            margin-right: 5px;
        `,
    },
    closeFilterIcon: {
        src: "X",
        color: "common.white",
        size: 16,
        css: css`
            cursor: pointer;
        `,
    },
};

const styles = trainingCalenderListStyles;

const GreyText = styled.span`
    color: ${color("common.ui3")};
    font-weight: 400;
    &:hover {
        &.primary-hover {
            color: ${color("primary.main")};
        }
    }
`;

const Seperator = styled.span`
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
    color: ${color("common.ui3")};
    ${xScreen("lte", "xs")} {
        margin: 0 7px 0;
        &.mobile-view {
            display: none;
        }
    }
`;

export const TrainingCalenderList: React.FC<Props> = ({
    events,
    facets,
    pagination,
    isLoading,
    selectedFilters,
    history,
    loadEvents,
    setEventFilters,
    xGoogleAnalytics,
    isAuthenticated,
}) => {
    const [SearchQuery, setSearchQuery] = React.useState("");
    const [value, setValue] = React.useState({ Topic: "", Locations: "", Duration: "", Credit: "", Price: "" });
    const [expanded, setExpanded] = React.useState(false);
    // const pdfLinks = useGetLinks(events.map(event=> event.Pdf).filter(Boolean),o=> o.fields.destination)

    React.useEffect(() => {
        loadEvents({});
    }, []);

    // Xng-Change
    // WES-1783
    const sendTracking = () => {
        const dataLayer = getDataLayer();

        if (!dataLayer || !xGoogleAnalytics.trainingFilterEvent) {
            return;
        }

        const googleAnalyticsData = {
            event: xGoogleAnalytics.trainingSearchEventName,
            query: SearchQuery,
            destination: window.location.href,
            affiliation: xGoogleAnalytics.affiliation,
            authentication_state: isAuthenticated ? "authenticated" : "unauthenticated",
        };

        dataLayer.push(googleAnalyticsData);
    };

    const handleInputChange = (event: any) => {
        debounce((query: string) => {
            setSearchQuery(query);
            loadEvents({ SearchQuery: query });
        }, 200)(event.target.value);
    };

    const changePage = (newPageIndex: number) => {
        loadEvents({ page: newPageIndex, SearchQuery });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadEvents({
            page: 1,
            pageSize: newPageSize,
            SearchQuery,
        });
    };

    const goToCourseDetailsClickHandler = (url: string) => {
        if (url) {
            history.push(url);
        }
    };
    const handleChange = (keyName: string, e: any) => {
        const newValue = { ...value, [keyName]: e.currentTarget.value };
        setValue(newValue);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={4} {...styles.accordion}>
                <AccordionSection title="Filters">
                    <UnorderedList {...styles.filteredSelection}>
                        {Object.entries(selectedFilters).map(([key, value]) => {
                            if (selectedFilters[key]) {
                                return (
                                    <ListItem {...styles.filteredSelectionItem} key={key}>
                                        <Icon
                                            {...styles.closeFilterIcon}
                                            onClick={() => setEventFilters({ name: "", parent: key })}
                                            role="button"
                                        />
                                        {`${key}: ${selectedFilters[key]}`}
                                    </ListItem>
                                );
                            }
                            return null;
                        })}
                    </UnorderedList>

                    {facets.map((facet: TrainingFacetsModel, index: number) => (
                        <StyledWrapper key={facet.Id} {...styles.mobileFilterWrapper}>
                            <Select
                                label={facet.Name}
                                value={selectedFilters[facet.Name]}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    setEventFilters({ name: e.currentTarget.value, parent: facets[index].Name });
                                    handleChange(facet.Name, e);
                                }}
                                placeholder={facet.Description}
                            >
                                <option value="">{facet.Description}</option>
                                {facet.Values.map((v: TrainingFacetValue) => (
                                    <option key={v.Id} value={v.Name}>
                                        {v.Name}
                                    </option>
                                ))}
                            </Select>
                        </StyledWrapper>
                    ))}
                </AccordionSection>
            </Accordion>
            <StyledWrapper {...styles.topSection}>
                <TextField
                    name="query"
                    label={translate("Search Training Courses")}
                    onChange={handleInputChange}
                    onBlur={sendTracking}
                    {...styles.searchInput}
                />
            </StyledWrapper>
            <GridContainer {...styles.outerContainer}>
                {!events?.length && (
                    <GridItem width={12}>
                        <Notify type="warning" showClose={false}>
                            {siteMessage("Custom_Events_NoEventsFound")}
                        </Notify>
                    </GridItem>
                )}
                {events?.map((trainingEvent: any) => (
                    <GridItem key={trainingEvent.Title + trainingEvent.Url} {...styles.gridItem}>
                        <StyledWrapper {...styles.eventCard}>
                            <StyledWrapper {...styles.eventInfo}>
                                <Clickable
                                    href={trainingEvent.Url}
                                    {...styles.eventTitle}
                                    aria-label="Event Title Clickable"
                                >
                                    <Typography>{trainingEvent.Title}</Typography>
                                </Clickable>
                                <StyledWrapper {...styles.eventTags}>
                                    {trainingEvent.Topic && (
                                        <Typography {...styles.eventTopic}>{trainingEvent.Topic}</Typography>
                                    )}
                                    {trainingEvent.Topic && <Seperator className="mobile-view">|</Seperator>}

                                    {trainingEvent.Tags.length > 0 && trainingEvent.Tags[0] !== "" && (
                                        <Typography as="strong">
                                            Tags:&nbsp;
                                            {trainingEvent.Tags.map((tag: string, index: number) => (
                                                <GreyText key={index}>
                                                    {tag}
                                                    {index !== trainingEvent.Tags.length - 1 && ", "}
                                                    {/* Add comma separator for all items except the last one */}
                                                </GreyText>
                                            ))}
                                        </Typography>
                                    )}
                                    {trainingEvent.Tags.length >= 1 && trainingEvent.Tags[0] !== "" && (
                                        <Seperator className="mobile-view">|</Seperator>
                                    )}
                                    <Link
                                        href={trainingEvent.Pdf}
                                        target="_blank"
                                        aria-label="PDF File Clickable"
                                        {...styles.pdfLink}
                                    >
                                        <GreyText className="primary-hover">View PDF</GreyText>
                                    </Link>
                                </StyledWrapper>
                                {trainingEvent.UpcomingDate && (
                                    <StyledWrapper {...styles.upcomingDateSection}>
                                        <Typography as="strong" {...styles.upcomingDateLabel}>
                                            {translate("Upcoming Dates")}:
                                        </Typography>
                                        <Typography as="p" {...styles.upcomingDate}>
                                            <Link href={trainingEvent.UpcomingDate.Url}>
                                                {trainingEvent.UpcomingDate.Location}
                                            </Link>
                                            {" , "}
                                            {new Date(trainingEvent.UpcomingDate.Date).toDateString()}
                                            {" , "}
                                            {trainingEvent.UpcomingDate.Time}
                                        </Typography>
                                    </StyledWrapper>
                                )}

                                <Typography as="p" {...styles.shortDescription}>
                                    {trainingEvent.ShortDescription}
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.eventActions}>
                                <Typography {...styles.eventPrice}>{trainingEvent.PriceDescription}</Typography>
                                <StyledWrapper {...styles.facets}>
                                    <span>{trainingEvent.DurationDescription}</span>
                                    {trainingEvent.DurationDescription && trainingEvent.CreditsDescription && (
                                        <Seperator>|</Seperator>
                                    )}
                                    <span>{trainingEvent.CreditsDescription}</span>
                                </StyledWrapper>
                                <Button
                                    {...styles.courseDetailsButton}
                                    onClick={() => goToCourseDetailsClickHandler(trainingEvent.Url)}
                                    aria-label="Course Details"
                                >
                                    {translate("Course Details")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                ))}
            </GridContainer>
            <StyledWrapper {...styles.pagination}>
                {pagination && !!events?.length && (
                    <Pagination
                        resultsCount={pagination.TotalItemCount}
                        currentPage={pagination.Page}
                        resultsPerPage={pagination.PageSize}
                        resultsPerPageOptions={pagination.PageSizeOptions}
                        onChangePage={changePage}
                        onChangeResultsPerPage={changeResultsPerPage}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(TrainingCalenderList)),
    definition: {
        group: "Xngage Custom" as any,
    },
};

export default widgetModule;
